import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { BosaCertificateRequest } from "./types";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCEU } from "@/lib/utils";
import {
  Award,
  Calendar,
  Clock,
  Download,
  FileCheck,
  FileX,
  Search,
  Upload,
  User,
  School,
  Filter,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const BosaRequests = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState<BosaCertificateRequest | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [certificateUrl, setCertificateUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');

  const {
    data: requests = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["bosa-requests"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("bosa_certificate_requests")
          .select(`
            *,
            certificates!inner (
              issue_date,
              course_id,
              courses:course_id (
                title,
                hours
              ),
              user_id
            )
          `)
          .order('request_date', { ascending: false });

        if (error) {
          throw error;
        }

        const requestsWithProfiles = await Promise.all(
          data.map(async (item: any) => {
            const { data: profileData, error: profileError } = await supabase
              .from("profiles")
              .select("first_name, last_name, position, school, school_district")
              .eq("id", item.certificates.user_id)
              .single();

            if (profileError) {
              console.error("Error fetching profile:", profileError);
            }

            return {
              id: item.id,
              user_id: item.certificates.user_id,
              certificate_id: item.certificate_id,
              request_date: item.request_date,
              status: item.status as 'pending' | 'approved' | 'rejected',
              admin_notes: item.admin_notes || undefined,
              user_notes: item.user_notes || undefined,
              approved_date: item.approved_date || undefined,
              rejected_date: item.rejected_date || undefined,
              approved_by: item.approved_by || undefined,
              bosa_certificate_url: item.bosa_certificate_url || undefined,
              created_at: item.created_at,
              updated_at: item.updated_at,
              profile: profileData ? {
                first_name: profileData.first_name || '',
                last_name: profileData.last_name || '',
                position: profileData.position || '',
                school: profileData.school || '',
                school_district: profileData.school_district || '',
              } : undefined,
              certificate: {
                course_title: item.certificates?.courses?.title || '',
                course_id: item.certificates?.course_id || '',
                completion_date: item.certificates?.issue_date || '',
                hours: item.certificates?.courses?.hours || 0,
              },
            };
          })
        );

        return requestsWithProfiles;
      } catch (error) {
        console.error("Error fetching BOSA requests:", error);
        toast({
          title: "Error",
          description: "Failed to load BOSA certificate requests",
          variant: "destructive",
        });
        return [];
      }
    },
  });

  const filteredRequests = requests.filter((request) => {
    if (statusFilter !== "all" && request.status !== statusFilter) {
      return false;
    }

    const searchText = `${request.profile?.first_name} ${request.profile?.last_name} ${request.certificate?.course_title}`.toLowerCase();
    return searchText.includes(searchQuery.toLowerCase());
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  const handleReview = (request: BosaCertificateRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || "");
    setCertificateUrl(request.bosa_certificate_url || "");
    setCertificateFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setReviewDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setCertificateFile(files[0]);
      setUploadStatus('idle');
    }
  };

  const uploadCertificate = async (): Promise<string | null> => {
    if (!certificateFile || !selectedRequest) return null;

    setIsUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(10);
    
    try {
      const fileExt = certificateFile.name.split('.').pop();
      const fileName = `${selectedRequest.id}_${Date.now()}.${fileExt}`;
      const filePath = `bosa_certificates/${fileName}`;
      
      const uploadProgress = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          clearInterval(uploadProgress);
          return prev;
        });
      }, 300);
      
      const { error: uploadError } = await supabase.storage
        .from('certificates')
        .upload(filePath, certificateFile);

      clearInterval(uploadProgress);
      
      if (uploadError) {
        throw uploadError;
      }

      setUploadProgress(100);
      setUploadStatus('success');
      
      const { data: { publicUrl } } = supabase.storage
        .from('certificates')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadStatus('error');
      toast({
        title: "Upload Failed",
        description: "Could not upload the certificate file.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      setIsProcessing(true);

      let finalCertificateUrl = certificateUrl;
      if (certificateFile) {
        const uploadedUrl = await uploadCertificate();
        if (uploadedUrl) {
          finalCertificateUrl = uploadedUrl;
        }
      }

      if (!finalCertificateUrl) {
        toast({
          title: "Certificate Required",
          description: "Please upload a BOSA certificate file or provide a URL.",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      const { error } = await supabase
        .from("bosa_certificate_requests")
        .update({
          status: "approved",
          admin_notes: adminNotes,
          bosa_certificate_url: finalCertificateUrl,
          approved_date: new Date().toISOString(),
          approved_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq("id", selectedRequest.id);

      if (error) throw error;

      toast({
        title: "Request Approved",
        description: "The BOSA certificate request has been approved successfully.",
      });

      setReviewDialogOpen(false);
      await refetch();
    } catch (error) {
      console.error("Error approving request:", error);
      toast({
        title: "Error",
        description: "Failed to approve the request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      setIsProcessing(true);

      const { error } = await supabase
        .from("bosa_certificate_requests")
        .update({
          status: "rejected",
          admin_notes: adminNotes,
          rejected_date: new Date().toISOString(),
        })
        .eq("id", selectedRequest.id);

      if (error) throw error;

      toast({
        title: "Request Rejected",
        description: "The BOSA certificate request has been rejected.",
      });

      setReviewDialogOpen(false);
      await refetch();
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast({
        title: "Error",
        description: "Failed to reject the request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="outline" className="bg-accent-light/20 text-primary border-accent-light">
            <AlertCircle className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="outline" className="bg-accent/10 text-accent border-accent/30">
            <CheckCircle className="mr-1 h-3 w-3" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="mr-1 h-3 w-3" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">Unknown</Badge>
        );
    }
  };

  return (
    <div className="container mx-auto p-8 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-title font-bold text-primary flex items-center gap-3">
          <Award className="h-8 w-8 text-primary" />
          BOSA Certificate Requests
        </h1>
        <p className="text-muted-foreground mt-2 font-subtitle">
          Manage BOSA certificate requests from participants
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-secondary/20 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-3 bg-gradient-to-r from-white to-accent-subtle/30">
            <CardTitle className="text-sm font-section font-medium flex items-center gap-2 text-primary">
              <Clock className="h-4 w-4 text-primary" />
              Pending Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-title font-bold text-primary">{pendingCount}</div>
            <p className="text-xs text-muted-foreground font-caption">Awaiting review</p>
          </CardContent>
        </Card>
        
        <Card className="border-secondary/20 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-3 bg-gradient-to-r from-white to-accent-subtle/30">
            <CardTitle className="text-sm font-section font-medium flex items-center gap-2 text-primary">
              <FileCheck className="h-4 w-4 text-accent" />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-title font-bold text-accent">{approvedCount}</div>
            <p className="text-xs text-muted-foreground font-caption">Certificates issued</p>
          </CardContent>
        </Card>

        <Card className="border-secondary/20 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-3 bg-gradient-to-r from-white to-accent-subtle/30">
            <CardTitle className="text-sm font-section font-medium flex items-center gap-2 text-primary">
              <FileX className="h-4 w-4 text-red-500" />
              Rejected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-title font-bold text-red-500">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground font-caption">Requests declined</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary/50" />
          <Input
            placeholder="Search by name or course..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 font-section border-accent-light/30 focus:ring-primary/20"
          />
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] font-section border-accent-light/30 focus:ring-primary/20">
              <Filter className="h-4 w-4 mr-2 text-primary/60" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="font-body">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-secondary/20 shadow-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-primary/5">
              <TableRow>
                <TableHead className="font-section text-primary">Status</TableHead>
                <TableHead className="font-section text-primary">Date Requested</TableHead>
                <TableHead className="font-section text-primary">Participant</TableHead>
                <TableHead className="font-section text-primary">Course</TableHead>
                <TableHead className="font-section text-primary">Hours</TableHead>
                <TableHead className="font-section text-primary">Completion Date</TableHead>
                <TableHead className="font-section text-primary text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 font-body">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
                      Loading requests...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 font-body">
                    {requests.length === 0 ? (
                      "No requests found"
                    ) : (
                      "No requests match your search criteria"
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-accent-subtle/10">
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="font-body text-primary/80">
                      {new Date(request.request_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-body text-primary/80">
                      {request.profile?.first_name} {request.profile?.last_name}
                    </TableCell>
                    <TableCell className="font-body max-w-[250px] truncate text-primary/80">
                      {request.certificate?.course_title}
                    </TableCell>
                    <TableCell className="font-body text-primary/80">
                      {formatCEU(request.certificate?.hours)}
                    </TableCell>
                    <TableCell className="font-body text-primary/80">
                      {request.certificate?.completion_date 
                        ? new Date(request.certificate.completion_date).toLocaleDateString() 
                        : "N/A"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="font-body border-primary/30 text-primary hover:bg-accent-subtle"
                        onClick={() => handleReview(request)}
                      >
                        {request.status === 'pending' ? 'Review' : 'View Details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedRequest && (
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-title text-primary">
                {selectedRequest.status === 'pending' 
                  ? "Review BOSA Certificate Request" 
                  : "BOSA Certificate Request Details"}
              </DialogTitle>
              <DialogDescription className="font-body">
                {selectedRequest.status === 'pending'
                  ? "Review and process this BOSA certificate request."
                  : `This request has been ${selectedRequest.status}.`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold font-section text-primary mb-1">Participant</h4>
                    <div className="text-sm font-body">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-accent" />
                        {selectedRequest.profile?.first_name} {selectedRequest.profile?.last_name}
                      </div>
                      <div className="flex items-center mt-1">
                        <School className="h-4 w-4 mr-2 text-accent" />
                        {selectedRequest.profile?.school || "N/A"}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold font-section text-primary mb-1">Request Details</h4>
                    <div className="text-sm font-body">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-accent" />
                        Requested: {new Date(selectedRequest.request_date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center mt-1">
                        {getStatusBadge(selectedRequest.status)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-semibold font-section text-primary mb-1">Course</h4>
                  <p className="text-sm font-body font-medium text-primary">{selectedRequest.certificate?.course_title}</p>
                  <div className="flex items-center gap-4 mt-1 text-sm font-body text-primary/70">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1 text-accent" />
                      {formatCEU(selectedRequest.certificate?.hours)} CEUs
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-accent" />
                      Completed: {selectedRequest.certificate?.completion_date 
                        ? new Date(selectedRequest.certificate.completion_date).toLocaleDateString() 
                        : "N/A"}
                    </div>
                  </div>
                </div>
                
                {selectedRequest.user_notes && (
                  <div>
                    <h4 className="text-sm font-semibold font-section text-primary mb-1">Participant Notes</h4>
                    <p className="text-sm font-body bg-accent-subtle/20 p-3 rounded-md text-primary/80">{selectedRequest.user_notes}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="text-sm font-semibold font-section text-primary mb-1">Admin Notes</h4>
                  <Textarea
                    placeholder="Add notes about this request..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="font-body border-accent-light/30 focus:ring-primary/20"
                    rows={3}
                    disabled={selectedRequest.status !== 'pending'}
                  />
                </div>
                
                {selectedRequest.status === 'pending' && (
                  <div>
                    <h4 className="text-sm font-semibold font-section text-primary mb-1">BOSA Certificate</h4>
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-secondary/30 rounded-lg p-6 text-center">
                        <label 
                          htmlFor="certificate-upload" 
                          className="flex flex-col items-center justify-center cursor-pointer"
                        >
                          <Upload className="h-10 w-10 text-secondary mb-3" />
                          <span className="text-sm font-medium font-body mb-1 text-primary">
                            {certificateFile ? certificateFile.name : "Upload BOSA Certificate"}
                          </span>
                          <span className="text-xs text-muted-foreground font-body mb-2">
                            Click to select a file (PDF or image)
                          </span>
                          <input
                            id="certificate-upload"
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={handleFileChange}
                            key={fileUploadKey}
                          />
                        </label>
                        
                        {uploadStatus !== 'idle' && (
                          <div className="mt-4 space-y-2">
                            <Progress value={uploadProgress} className="h-2 w-full bg-accent-light/30" 
                              indicatorClassName="bg-accent" />
                            <p className="text-xs text-center font-body text-primary/80">
                              {uploadStatus === 'uploading' && 'Uploading certificate...'}
                              {uploadStatus === 'success' && 'Upload successful!'}
                              {uploadStatus === 'error' && 'Upload failed. Please try again.'}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-center text-muted-foreground font-body">- OR -</p>
                      
                      <div>
                        <Input
                          placeholder="Enter URL to the BOSA certificate document"
                          value={certificateUrl}
                          onChange={(e) => setCertificateUrl(e.target.value)}
                          className="font-body border-accent-light/30 focus:ring-primary/20"
                        />
                        <p className="text-xs text-muted-foreground mt-1 font-body">
                          Enter a URL to the BOSA certificate document if you have one hosted elsewhere
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedRequest.status === 'approved' && selectedRequest.bosa_certificate_url && (
                  <div>
                    <h4 className="text-sm font-semibold font-section text-primary mb-1">BOSA Certificate</h4>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 overflow-hidden">
                        <Input
                          value={selectedRequest.bosa_certificate_url}
                          className="font-body text-ellipsis border-accent-light/30"
                          readOnly
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => window.open(selectedRequest.bosa_certificate_url, '_blank')}
                        title="Download certificate"
                        className="border-accent text-accent hover:bg-accent/10"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {selectedRequest.approved_date && (
                      <p className="text-xs text-muted-foreground mt-2 font-body">
                        Approved on {new Date(selectedRequest.approved_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              {selectedRequest.status === 'pending' ? (
                <>
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleApprove}
                    disabled={isProcessing || isUploading || (!certificateFile && !certificateUrl)}
                    className="font-body bg-accent hover:bg-accent/90 text-white"
                  >
                    {isProcessing || isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isUploading ? "Uploading..." : "Processing"}
                      </>
                    ) : (
                      <>
                        <FileCheck className="mr-2 h-4 w-4" />
                        Approve
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReject}
                    disabled={isProcessing}
                    className="font-body text-red-500 hover:bg-red-50 hover:text-red-600 border-red-200"
                  >
                    <FileX className="mr-2 h-4 w-4" />
                    Reject
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setReviewDialogOpen(false)}
                  className="font-body border-primary/30 text-primary hover:bg-accent-subtle"
                >
                  Close
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BosaRequests;
